import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDarkMode } from "../DarkModeContext";
import DarkModeToggle from "../DarkModeToggle";
import { useSettingsModal } from "../../contexts/SettingsModalContext";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useIsLocalhost } from "../../store/environmentStore";
import HelpSystem from "../tour/HelpSystem";
import ViewPromptsModal from "../admin/ViewPromptsModal";
import {
  Shield,
  LogOut,
  ChevronDown,
  User,
  MessageSquare,
  Code,
  Settings,
  UserCog,
  FolderOpen,
  Menu,
  X,
  FileCode2,
  MoreVertical,
  Moon,
  Sun,
  Bug,
  Upload,
  BookOpen,
  Sparkles,
} from "lucide-react";
import BugReportButton from "../BugReportButton";
import NotificationIcon from "../NotificationIcon";
import OrganizationSwitcherSection from "../organization/OrganizationSwitcherSection";
import { useSubscriptionAccess } from "../../hooks/useSubscriptionAccess";
import { useMobileNav } from "../../contexts/MobileNavContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import ApplicationSidebar from "../layouts/ApplicationSidebar";
import FindingsListSidebar from "../layouts/FindingsListSidebar";
import ComplianceSidebar from "../layouts/ComplianceSidebar";
import PostHogService, { DemoEvent } from "../../posthog";

interface HeaderProps {
  type?: "homepage" | "app";
  showNavigation?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  condition?: boolean;
}

interface RouteInfo {
  workspaceId: string;
  isThreadView: boolean;
  threadId: string;
  activeSection: string;
  projectBasePath: string;
}

const Header: React.FC<HeaderProps> = ({
  type = "app",
  showNavigation = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { user, isAuthenticated, logout, isLoading, isAdmin, displayName } = useAuth();
  const api = useAuthenticatedApi();
  const { openAccountSettings, openProfileModal } = useSettingsModal();
  const { accessType } = useSubscriptionAccess();
  const { currentOrganization } = useOrganization();
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // State management
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const {
    isOpen: mobileMenuOpen,
    close: closeMobileMenu,
    toggle: toggleMobileMenu,
    sidebarVariant: mobileSidebarVariant,
  } = useMobileNav();
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [projectName, setProjectName] = useState<string>("");
  const isLocalhost = useIsLocalhost();
  const [viewPromptsModalOpen, setViewPromptsModalOpen] = useState(false);
  const [profilePictureError, setProfilePictureError] = useState(false);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isDemoWorkspace = !!currentOrganization?.is_demo;

  const handleDemoConnectRepoClick = () => {
    PostHogService.track(DemoEvent.CONNECT_REPO_CLICKED, {
      organization_id: currentOrganization?.id ?? null,
      path: `${location.pathname}${location.search}`,
      destination: '/onboarding/connect',
    });
    navigate('/onboarding/connect');
  };

  // Extract route information
  const routeInfo = React.useMemo((): RouteInfo => {
    const pathParts = location.pathname.split("/");
    const hasWorkspaceId =
      pathParts.includes("project") &&
      pathParts[pathParts.indexOf("project") + 1];
    const workspaceId = hasWorkspaceId
      ? pathParts[pathParts.indexOf("project") + 1]
      : "";
    const isThreadView = pathParts.includes("thread");
    const threadId =
      isThreadView && pathParts[pathParts.indexOf("thread") + 1]
        ? pathParts[pathParts.indexOf("thread") + 1]
        : "";

    const projectBasePath = location.pathname.startsWith("/project/")
      ? location.pathname.split(`/project/${workspaceId}/`)[1] || ""
      : "";

    const activeSection = (() => {
      if (isThreadView) {
        const urlParams = new URLSearchParams(location.search);
        const tab = urlParams.get("tab") || "chat";
        return `thread-${tab}`;
      }
      if (projectBasePath === "code") return "main-code";
      if (projectBasePath === "security") return "main-security";
      if (
        projectBasePath === "" ||
        projectBasePath === "chat" ||
        projectBasePath.startsWith("thread/")
      )
        return "main-chat";
      return "";
    })();

    return {
      workspaceId,
      isThreadView,
      threadId,
      activeSection,
      projectBasePath,
    };
  }, [location]);

  // Auto-close the mobile drawer when the route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Store the last visited thread for the current workspace in session storage
  useEffect(() => {
    if (routeInfo.workspaceId && routeInfo.isThreadView && routeInfo.threadId) {
      try {
        // Save the last thread ID for this workspace in session storage
        sessionStorage.setItem(
          `lastThreadVisited_${routeInfo.workspaceId}`,
          routeInfo.threadId,
        );
      } catch (err) {
        console.error("Error saving last thread to session storage:", err);
      }
    }
  }, [routeInfo.workspaceId, routeInfo.isThreadView, routeInfo.threadId]);

  // Navigation items configuration
  const mainNavItems: NavItem[] = React.useMemo(
    () =>
      [
        // { id: 'main-chat', label: 'Chat', icon: MessageSquare, path: `/project/${routeInfo.workspaceId}` },
        {
          id: "main-security",
          label: "Security",
          icon: Shield,
          path: `/project/${routeInfo.workspaceId}/security`,
          condition: isLocalhost,
        },
      ].filter((item) => item.condition),
    [routeInfo.workspaceId, isLocalhost],
  );

  const threadNavItems: NavItem[] = React.useMemo(
    () =>
      [
        {
          id: "thread-chat",
          label: "Chat",
          icon: MessageSquare,
          path: `${location.pathname}?tab=chat`,
          condition: isLocalhost,
        },
      ].filter((item) => item.condition),
    [location.pathname],
  );

  useEffect(() => {
    const fetchWorkspaceName = async () => {
      if (routeInfo.workspaceId) {
        try {
          const response = await api.get(
            `/workspaces/${routeInfo.workspaceId}`,
          );
          const data = response.data || response;
          setProjectName(data.name || "");
          document.title = data.name
            ? `${data.name} | Kolega.dev`
            : "Kolega.dev";
        } catch (err) {
          console.error("Error fetching workspace name:", err);
        }
      } else {
        document.title = "Kolega.dev";
      }
    };

    fetchWorkspaceName();
  }, [routeInfo.workspaceId, api]);

  // Preload profile picture when user changes
  useEffect(() => {
    setProfilePictureError(false);

    if (user?.picture) {
      const img = new Image();
      img.referrerPolicy = 'no-referrer';
      img.onerror = () => setProfilePictureError(true);
      img.src = user.picture;
    }
  }, [user?.picture]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        mobileActionsRef.current &&
        !mobileActionsRef.current.contains(event.target as Node)
      ) {
        setMobileActionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper functions
  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const getUserDisplayName = () => displayName || "User";
  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleNavigation = (path: string, isThreadNav: boolean = false) => {
    setTimeout(() => {
      closeMobileMenu();
      setUserMenuOpen(false);
    }, 100);

    if (isThreadNav && location.pathname.includes("/threads")) {
      const tabMatch = path.match(/[?&]tab=([^&]+)/);
      if (tabMatch) {
        const tab = tabMatch[1];
        const urlParams = new URLSearchParams(location.search);
        urlParams.set("tab", tab);
        navigate(`${location.pathname}?${urlParams.toString()}`);
      } else {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  };

  /**
   * Navigate to the last visited thread for the current workspace
   * Falls back to the main project view if no thread was visited
   */
  const navigateToLastThread = () => {
    if (!routeInfo.workspaceId) return;

    try {
      // Get the last thread ID for this workspace from session storage
      const lastThreadId = sessionStorage.getItem(
        `lastThreadVisited_${routeInfo.workspaceId}`,
      );

      if (lastThreadId) {
        // Navigate to that thread
        navigate(`/project/${routeInfo.workspaceId}/thread/${lastThreadId}`);
      } else {
        // If no thread was visited, navigate to the main project view
        navigate(`/project/${routeInfo.workspaceId}`);
      }
    } catch (err) {
      console.error("Error retrieving last thread from session storage:", err);
      // Fallback to main project view
      navigate(`/project/${routeInfo.workspaceId}`);
    }
  };

  // Navigation components
  const NavigationItems = ({
    items,
    isThread = false,
  }: {
    items: NavItem[];
    isThread?: boolean;
  }) => (
    <div className="flex items-center space-x-3">
      {items.map((item) => (
        <div key={item.id} className="relative group">
          <button
            className={`group-item flex items-center px-3 py-1.5 text-sm transition-all duration-200 rounded-md ${
              routeInfo.activeSection === item.id
                ? "text-ui-text-primary font-medium"
                : "hover:bg-ui-background-tertiary text-ui-text-secondary hover:text-ui-text-primary"
            }`}
            onClick={() => handleNavigation(item.path, isThread)}
            data-tour={
              isThread
                ? item.id === "thread-chat"
                  ? "thread-chat-tab"
                  : item.id === "thread-tasks"
                    ? "thread-tasks-tab"
                    : ""
                : item.id === "main-chat"
                  ? "chat-nav-button"
                  : item.id === "main-code"
                    ? "code-nav-button"
                    : item.id === "main-security"
                      ? "security-nav-button"
                      : ""
            }
          >
            <span className="inline-flex items-center">
              <item.icon size={14} className="mr-1.5" />
              <span className="hidden sm:inline">{item.label}</span>
            </span>
          </button>
          <div
            className={`absolute bottom-0 h-0.5 bg-brand-primaryDark dark:bg-brand-primaryDarkMode transition-all duration-200 ${
              routeInfo.activeSection === item.id
                ? "left-1/2 transform -translate-x-1/2 w-[calc(100%-1.5rem)]"
                : "group-hover:w-full left-0 w-0"
            }`}
          />
        </div>
      ))}
    </div>
  );

  const UserAvatar = React.useMemo(() => {
    if (user?.picture && !profilePictureError) {
      return (
        <div
          className="w-8 h-8 rounded-full bg-cover bg-center flex-none"
          style={{ backgroundImage: `url(${user.picture})` }}
          title="User profile"
          role="img"
          aria-label="User profile"
        />
      );
    }
    const initials = (displayName || 'U')
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return (
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-none text-white font-bold text-[13px]"
        style={{ background: 'linear-gradient(135deg, var(--avatar-from), var(--avatar-to))' }}
        title="User"
      >
        {initials}
      </div>
    );
  }, [user?.picture, profilePictureError, displayName]);

  const UserMenu = () => (
    <div className="relative" ref={userMenuRef}>
      <button
        className="flex items-center gap-2 py-1 px-1.5 rounded-[10px] transition-colors hover:bg-[var(--surface-2)]"
        style={{ color: 'var(--text-primary)' }}
        onClick={() => {
          setUserMenuOpen(!userMenuOpen);
          closeMobileMenu(); // Close mobile menu when opening user menu
        }}
        disabled={isLoading}
        data-tour="user-menu-button"
        title="User menu"
      >
        {UserAvatar}
        <span className="hidden sm:flex flex-col items-start min-w-0 leading-tight">
          <span className="max-w-32 truncate text-[13px] font-semibold">
            {isAuthenticated ? getUserDisplayName() : "User"}
          </span>
          {isAuthenticated && currentOrganization && (
            <span className="max-w-32 truncate text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {currentOrganization.name}
            </span>
          )}
        </span>
        <ChevronDown size={12} className="hidden sm:block" style={{ color: 'var(--text-muted)' }} />
      </button>

      {userMenuOpen && (
        <div className="absolute right-0 mt-1 py-1 rounded-lg shadow-lg shadow-neutral-300/30 dark:shadow-black/20 w-64 z-50 bg-ui-background-secondary border border-ui-border-primary text-ui-text-primary max-h-[calc(100vh-80px)] overflow-y-auto">
          {isAuthenticated && user ? (
            <>
              <div className="px-3 py-2 border-b border-ui-border-primary">
                <div className="text-sm font-medium text-ui-text-primary truncate">
                  {displayName || "User"}
                </div>
                <div className="text-xs text-ui-text-tertiary truncate">
                  {user.email}
                </div>
              </div>

              {currentOrganization && (
                <div className="border-b border-ui-border-primary py-1">
                  <OrganizationSwitcherSection
                    onAfterAction={() => setUserMenuOpen(false)}
                  />
                </div>
              )}

              {isAdmin && (
                <div className="border-b border-ui-border-primary py-1">
                  <button
                    key="admin-link"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/admin");
                    }}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
                    title="Administration"
                  >
                    <UserCog size={14} className="mr-1.5" />
                    Administration
                  </button>
                  {routeInfo.workspaceId && (
                    <button
                      key="view-prompts"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setViewPromptsModalOpen(true);
                      }}
                      className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
                      title="View Prompts"
                    >
                      <FileCode2 size={14} className="mr-1.5" />
                      View Prompts
                    </button>
                  )}
                </div>
              )}

              <div className="py-1">
                <button
                  key="profile"
                  onClick={() => {
                    setUserMenuOpen(false);
                    openProfileModal();
                  }}
                  className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
                  title="Profile settings"
                >
                  <User size={14} className="mr-1.5" />
                  Profile
                </button>
                <button
                  key="account"
                  onClick={() => {
                    setUserMenuOpen(false);
                    openAccountSettings();
                  }}
                  className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
                  title="Account settings"
                >
                  <Settings size={14} className="mr-1.5" />
                  Settings
                </button>
                <div className="border-t border-ui-border-primary pt-1 mt-1">
                  <button
                    key="sign-out"
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleSignOut();
                    }}
                    disabled={isLoading}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    title="Sign out"
                  >
                    <LogOut size={14} className="mr-1.5" />
                    {isLoading ? "Signing out..." : "Sign Out"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="px-3 py-2">
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/");
                }}
                className="block w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors rounded"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderMobileContextualSidebar = () => {
    switch (mobileSidebarVariant) {
      case 'application':
        return <ApplicationSidebar />;
      case 'findings':
        return <FindingsListSidebar />;
      case 'compliance':
        return <ComplianceSidebar />;
      default:
        return null;
    }
  };

  const MobileMenu = () => {
    const contextualSidebar = renderMobileContextualSidebar();

    return (
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-40 md:hidden bg-black/40 transition-opacity duration-200 ${
            mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />

        {/* Full-height slide-in drawer on the left */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-[86vw] max-w-sm md:hidden flex flex-col bg-ui-background-primary shadow-xl border-r border-ui-border-primary transform transition-transform duration-200 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          {/* Drawer header with close button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-ui-border-primary">
            <span className="text-sm font-semibold text-ui-text-primary">
              Menu
            </span>
            <button
              type="button"
              onClick={closeMobileMenu}
              className="p-1.5 rounded-lg text-ui-text-tertiary hover:bg-ui-background-tertiary dark:text-ui-text-tertiary dark:hover:bg-ui-background-tertiary"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            {/* Contextual sidebar for the current section */}
            {contextualSidebar && (
              <div className="border-t border-ui-border-primary [&>aside]:w-full [&>aside]:border-r-0">
                {contextualSidebar}
              </div>
            )}

            {/* Action items */}
            <div className="border-t border-ui-border-primary p-3 space-y-1">
              <button
                onClick={() => {
                  toggleDarkMode();
                  closeMobileMenu();
                }}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors rounded-lg"
              >
                {isDarkMode ? (
                  <Sun size={18} className="mr-2" />
                ) : (
                  <Moon size={18} className="mr-2" />
                )}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>

              <a
                href="https://kolega.dev/docs"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors rounded-lg"
                title="Help Center"
                aria-label="Open Help Center"
              >
                <BookOpen size={18} className="mr-2" />
                Help Center
              </a>

              <button
                onClick={() => {
                  closeMobileMenu();
                  const bugReportBtn = document.querySelector(
                    '[data-bug-report-trigger]',
                  ) as HTMLButtonElement;
                  if (bugReportBtn) bugReportBtn.click();
                }}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors rounded-lg"
              >
                <Bug size={18} className="mr-2" />
                Report Bug
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Don't show header on login page
  if (location.pathname === "/login") return null;

  return (
    <>
    <header
      className="flex items-center justify-between gap-4 px-4 sm:px-[22px] h-14 sticky top-0 z-30 w-full transition-colors duration-200"
      style={{
        background: 'var(--bg-raised)',
        borderBottom: '1px solid var(--divider)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Logo Section */}
      <div className="flex items-center">
        <div
          className="flex items-center cursor-pointer group"
          style={{ marginRight: "1rem" }}
          onClick={() => {
            if (type === "homepage") {
              window.scrollTo(0, 0);
            } else {
              navigate("/repositories");
            }
          }}
        >
          <h3 className="text-base sm:text-lg font-bold flex items-center">
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-1.5 sm:mr-2"
            >
              <path
                d="M7 8L3 12L7 16"
                stroke="var(--accent-ink)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17 8L21 12L17 16"
                stroke="var(--accent-ink)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 20L14 4"
                stroke="var(--accent-ink)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Kolega&nbsp;</span>
            <span style={{ color: 'var(--accent-ink)' }}>DevSec</span>
          </h3>
        </div>

      </div>

      {/* Navigation links for unauthenticated users - Centered */}
      {!isAuthenticated && (
        <nav className="flex items-center justify-center flex-1 gap-6">
          <a
            href="https://kolega.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-medium rounded-lg transition-all text-ui-text-secondary hover:bg-ui-background-tertiary hover:text-ui-text-primary"
          >
            Home
          </a>
          {/* When the on-domain public pages are enabled, keep "Pricing" on this domain so
              it lands on the compliant pricing page (Paddle reviews this domain). */}
          {process.env.REACT_APP_SHOW_PUBLIC_PAGES === 'true' ? (
            <a
              href="/pricing"
              className="px-3 py-1.5 text-sm font-medium rounded-lg transition-all text-ui-text-secondary hover:bg-ui-background-tertiary hover:text-ui-text-primary"
            >
              Pricing
            </a>
          ) : (
            <a
              href="https://kolega.dev/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-sm font-medium rounded-lg transition-all text-ui-text-secondary hover:bg-ui-background-tertiary hover:text-ui-text-primary"
            >
              Pricing
            </a>
          )}
        </nav>
      )}

      {/* Right Side Actions */}
      <div className="flex items-center gap-2 sm:gap-3">
        {isAuthenticated && isDemoWorkspace ? (
          <button
            onClick={handleDemoConnectRepoClick}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold transition-all active:scale-[0.98]"
            style={{ background: 'var(--lime)', color: 'var(--lime-fg)', borderRadius: 'var(--radius-md)' }}
            title="Connect your repository"
          >
            <FileCode2 size={15} />
            <span className="hidden sm:inline">Connect Your Repo</span>
            <span className="sm:hidden">Connect</span>
          </button>
        ) : isAuthenticated && (accessType === 'free' || accessType === 'trial') ? (
          <button
            onClick={() => openAccountSettings('plan')}
            className="hidden sm:flex items-center gap-2 cursor-pointer transition-all"
            style={{
              background: 'var(--lime-soft)',
              border: '1px solid var(--lime-soft-border)',
              borderRadius: 9,
              padding: '4px 5px 4px 11px',
            }}
            title="Upgrade your plan"
          >
            <span className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: 'var(--accent-ink)' }}>
              <Sparkles size={13} />
              {accessType === 'trial' ? 'Free trial' : 'Free plan'}
            </span>
            <span
              className="text-[12px] font-bold"
              style={{ background: 'var(--lime)', color: 'var(--lime-fg)', borderRadius: 6, padding: '3px 9px' }}
            >
              Upgrade
            </span>
          </button>
        ) : null}

        {/* Help Center */}
        <a
          href="https://kolega.dev/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center text-sm transition-colors hover:opacity-80"
          style={{ color: 'var(--text-secondary)' }}
          title="Help Center"
          aria-label="Open Help Center"
        >
          Help Center
        </a>

        {/* Notifications - Always visible */}
        {isAuthenticated && <NotificationIcon />}

        {/* Desktop-only actions */}
        {!isMobile && (
          <>
            {/* Bug Report Button */}
            {type === "app" && <BugReportButton />}

            {/* Dark Mode Toggle */}
            <DarkModeToggle
              isDarkMode={isDarkMode}
              toggleDarkMode={toggleDarkMode}
            />
          </>
        )}

        {/* User Menu - Always visible on both mobile and desktop */}
        {type === "app" && <UserMenu />}

        {/* Mobile Menu Button and Dropdown */}
        {type === "app" && isAuthenticated && (
          <div className="md:hidden ml-1" ref={menuRef}>
            <button
              className="p-1.5 sm:p-2 rounded-lg hover:bg-ui-background-tertiary transition-colors"
              onClick={() => {
                toggleMobileMenu();
                setUserMenuOpen(false);
              }}
              aria-label="Open menu"
              title="Open menu"
            >
              <Menu size={18} className="sm:w-5 sm:h-5" />
            </button>
            <MobileMenu />
          </div>
        )}
      </div>

      {/* Help System */}
      <HelpSystem pageName="header" />

      {/* View Prompts Modal */}
      {viewPromptsModalOpen && routeInfo.workspaceId && (
        <ViewPromptsModal
          isOpen={viewPromptsModalOpen}
          onClose={() => setViewPromptsModalOpen(false)}
          workspaceId={routeInfo.workspaceId}
        />
      )}
    </header>

    </>
  );
};

export default Header;
